import { z } from 'zod';
import { parseLanguageTag } from './language.js';
/**
 * Field limits of `glossary_entries` (spec 20 §4); API validation, the
 * database CHECK and the web editor use the same numbers.
 */
export const GLOSSARY_LIMITS = {
    termMaxLength: 120,
    translationMaxLength: 120,
    noteMaxLength: 500,
    /** Languages one entry may carry. */
    maxTranslations: 50,
};
/**
 * Issue messages are the API's `errors[].code` values; the issue path is the
 * key as sent (`["czech"]`), so the API reports the field `translations.czech`.
 */
export const GLOSSARY_TRANSLATION_ISSUES = ['invalid_language', 'duplicate_language', 'invalid_length', 'too_many'];
function translationsSchema(allowNull) {
    return z.record(z.string(), z.unknown()).transform((input, context) => {
        const output = {};
        for (const [key, value] of Object.entries(input)) {
            const tag = parseLanguageTag(key);
            if (tag === null) {
                context.addIssue({ code: 'custom', path: [key], message: 'invalid_language' });
                continue;
            }
            // `de-at` and `de-AT` name the same language: one of them only.
            if (Object.hasOwn(output, tag)) {
                context.addIssue({ code: 'custom', path: [key], message: 'duplicate_language' });
                continue;
            }
            if (value === null && allowNull) {
                output[tag] = null;
                continue;
            }
            const text = typeof value === 'string' ? value.trim() : '';
            if (text.length < 1 || text.length > GLOSSARY_LIMITS.translationMaxLength) {
                context.addIssue({ code: 'custom', path: [key], message: 'invalid_length' });
                continue;
            }
            output[tag] = text;
        }
        if (Object.keys(output).length > GLOSSARY_LIMITS.maxTranslations) {
            context.addIssue({ code: 'custom', path: [], message: 'too_many' });
        }
        return output;
    });
}
/**
 * Translations of a new entry: keys are BCP-47 tags (stored canonical,
 * `de-at` → `de-AT`), values 1-120 characters after trimming. A value equal to
 * the term means "keep as is" (brand names).
 */
export const glossaryTranslationsSchema = translationsSchema(false);
/** Translations in a `PATCH`: like {@link glossaryTranslationsSchema}, and `null` removes a language. */
export const glossaryTranslationsPatchSchema = translationsSchema(true);
/**
 * Applies a per-key merge patch (RFC 7396 on one level): a value sets or
 * replaces that language, `null` removes it, absent keys stay.
 */
export function mergeGlossaryTranslations(current, patch) {
    const merged = { ...current };
    for (const [tag, value] of Object.entries(patch)) {
        if (value === null)
            delete merged[tag];
        else
            merged[tag] = value;
    }
    return merged;
}
//# sourceMappingURL=glossary.js.map